import { inject, Injectable } from '@angular/core';
import { ENVIRONMENT } from '@marmon/frontend/common';
import {
	fetchAndActivate,
	getBoolean,
	getNumber,
	getString,
	getValue,
	RemoteConfig,
} from '@marmon/frontend/firebase/remote-config';
import {
	RemoteConfigBooleanKey,
	RemoteConfigKey,
	RemoteConfigNumberKey,
	RemoteConfigStringKey,
} from '@marmon/shared/types/config';

const OneDayMillis = 24 * 60 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class ConfigService {
	private readonly config = inject(RemoteConfig);
	private readonly environment = inject(ENVIRONMENT, { optional: true });

	readonly check = () => {
		if (this.environment?.emulators) {
			return Promise.resolve();
		}

		this.config.settings.minimumFetchIntervalMillis = 0;
		if (!this.isInvalidated(this.config.fetchTimeMillis)) {
			return Promise.resolve();
		}

		return fetchAndActivate(this.config);
	};

	readonly getNumber = (key: RemoteConfigNumberKey) =>
		this.check().then(() => getNumber(this.config, key));

	readonly getString = (key: RemoteConfigStringKey) =>
		this.check().then(() => getString(this.config, key));

	readonly getBoolean = (key: RemoteConfigBooleanKey) =>
		this.check().then(() => getBoolean(this.config, key));

	readonly getValue = (key: RemoteConfigKey) =>
		this.check().then(() => getValue(this.config, key));

	isInvalidated(millis: number) {
		const now = new Date();
		const lastFetch = new Date(millis);
		return Math.abs(now.getTime() - lastFetch.getTime()) >= OneDayMillis;
	}
}
